'use client';

import { useTranslations } from 'next-intl';
import { SectionWrapper } from '@/components/ui/section-wrapper';
import { Typography } from '@/components/ui/typography';
import { Card } from '@/components/ui/card';
import { SectionHeading } from '@/components/frontend/home/section-heading';
import { useScrollAnimation } from '@/hooks/use-scroll-animation';
import { Users, Target, Zap } from 'lucide-react';

interface CountryStepsSectionProps {
    translationNamespace: string;
}

export function CountryStepsSection({ translationNamespace }: CountryStepsSectionProps) {
    const t = useTranslations(translationNamespace);
    const sectionRef = useScrollAnimation({ threshold: 0.15 });

    const steps = [
        {
            icon: Users,
            title: t('steps.step1.title'),
            description: t('steps.step1.description'),
        },
        {
            icon: Target,
            title: t('steps.step2.title'),
            description: t('steps.step2.description'),
        },
        {
            icon: Zap,
            title: t('steps.step3.title'),
            description: t('steps.step3.description'),
        },
    ];

    return (
        <SectionWrapper 
            ref={sectionRef.ref}
            variant="gradient" 
            padding="md" 
            className="bg-gray-50 dark:bg-gray-900"
        >
            <div className={`container mx-auto transition-all duration-700 ${
                sectionRef.isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
            }`}>
                <SectionHeading
                    pill={t('steps.pill')}
                    title={t('steps.title')}
                    subtitle={t('steps.subtitle')}
                    align="center"
                    maxWidthClass="max-w-4xl"
                    isVisible={sectionRef.isVisible}
                />
                <div className="grid md:grid-cols-3 gap-8 mt-8">
                    {steps.map((step, index) => {
                        const Icon = step.icon;
                        return (
                            <div
                                key={index}
                                className={`transition-all duration-700 ${
                                    sectionRef.isVisible 
                                        ? 'opacity-100 translate-y-0' 
                                        : 'opacity-0 translate-y-6'
                                }`}
                                style={{ transitionDelay: `${200 + index * 100}ms` }}
                            >
                                <Card
                                    title=""
                                    description=""
                                    className="h-full"
                                    animationDelay={index * 100}
                                    renderContent={
                                        <div className="flex items-start gap-4">
                                            <div className="flex-shrink-0 w-12 h-12 rounded-lg bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center">
                                                <Icon className="w-6 h-6 text-primary-600 dark:text-primary-400" />
                                            </div>
                                            <div className="flex-1">
                                                <Typography variant="h3" className="text-xl font-semibold mb-2 text-gray-900 dark:text-white">
                                                    {step.title}
                                                </Typography>
                                                <Typography variant="p" className="text-gray-600 dark:text-gray-400">
                                                    {step.description}
                                                </Typography>
                                            </div>
                                        </div>
                                    }
                                />
                            </div>
                        );
                    })}
                </div>
            </div>
        </SectionWrapper>
    );
}

